// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';

export type Status =
  | 'Offered'
  | 'Offered2step'
  | 'Requested2Step'
  | 'Rejected2Step'
  | 'Executed2Step'
  | 'Executed'
  | 'Pending'
  | 'Failed'
  | 'Rejected'
  | 'Accepted'
  | 'Instructed'
  | 'Allocated'
  | 'Settled'
  | 'Requested'
  | 'Change Requested';

interface StateCellProps {
  state: Status;
  reason?: string;
  popover?: React.ReactNode;
}

const stateColor: Record<Status, string> = {
  Executed: 'success.main',
  Executed2Step: 'success.main',
  Pending: 'warning.main',
  Failed: 'error.main',
  Rejected: 'error.main',
  Rejected2Step: 'error.main',
  Allocated: 'warning.main',
  Accepted: 'success.main',
  Offered: 'warning.main',
  Offered2step: 'warning.main',
  Requested2Step: 'warning.main',
  Instructed: 'warning.main',
  Settled: 'success.main',
  Requested: 'warning.main',
  'Change Requested': 'warning.main',
};
const stateLabelMap: Partial<Record<Status, string>> = {
  Offered2step: 'Offered',
  Requested2Step: 'Requested',
  Rejected2Step: 'Rejected',
  Executed2Step: 'Executed',
};
export const StateCell: React.FC<StateCellProps> = ({ state, reason, popover }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const isFailedOrRejected = state === 'Failed' || state === 'Rejected';
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  if (!isFailedOrRejected) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Typography
          fontWeight="bold"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          color={stateColor[state]}
          variant="caption"
        >
          {stateLabelMap[state] || state}
        </Typography>
        {popover && (
          <IconButton onClick={handleClick} size="small">
            <InfoOutlined sx={{ color: stateColor[state] }} fontSize="small" />
          </IconButton>
        )}
        {popover && (
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {popover}
          </Popover>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography fontWeight="bold" color="error.main" variant="caption">
          {stateLabelMap[state] || state}
        </Typography>
        <Tooltip title="Click for more info">
          <IconButton size="small" onClick={handleClick}>
            <InfoOutlined color="error" fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {!!reason?.length && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Typography variant="body2" sx={{ p: 1 }}>
            {reason}
          </Typography>
        </Popover>
      )}
    </Box>
  );
};
